import { MEMBERS_TAB_VALUE } from '@/constants/stringVars';
import { TMemberCare, TMemberCareTabValue } from '@/types/MemberCare.types';
import { TUser } from '@/types/User.types';
import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';

type TAppStoreState = {
  user: TUser | null;
  setUser: (user: TUser) => void;
  memberCare: TMemberCare | null;
  setMemberCare: (memberCare: TMemberCare) => void;
  setAppStoreOnRehydrate: (bundle: TSessionBundle) => void;
  memberCareMembersTab: TMemberCareTabValue;
  setMemberCareMembersTab: (newTab: TMemberCareTabValue) => void;
  unreadNotifications: number | undefined;
  setUnreadNotifications: (newUnreadNotifications: number) => void;
  reset: () => void;
};

/**
 * A boot-time rehydration payload. memberCare is optional, because its fetch may
 * have failed and a slice that did not resolve must be left alone rather than
 * written as empty — see setAppStoreOnRehydrate.
 */
export type TSessionBundle = {
  user: TUser;
  memberCare?: TMemberCare | null;
};

const initialState = {
  user: null,
  memberCare: null,
  memberCareMembersTab: MEMBERS_TAB_VALUE as TMemberCareTabValue,
  unreadNotifications: undefined,
};

/**
 * NOTE: Example of a store that requires persistence and subscription.
 * State will persis for user even after page refresh.
 * Use this as a reference when creating a new store.
 */
export const useAppStore = create<TAppStoreState>()(
  subscribeWithSelector(
    persist(
      (set) => ({
        ...initialState,
        setUser: (user) => {
          set((state) => {
            return {
              ...state,
              user,
            };
          });
        },
        setMemberCare: (memberCare) => {
          set((state) => {
            return {
              ...state,
              memberCare,
            };
          });
        },
        setAppStoreOnRehydrate: (bundle) => {
          set((state) => {
            const { user, memberCare } = bundle;

            // Only preserve existing slices when they belong to the same person.
            // Nothing here is partialized, so localStorage can hold user A while
            // the cookie now verifies user B — degrading a failed fetch would then
            // hand B a slice of A's data. The boot check blocks on a retry state in
            // that case rather than calling this with a partial bundle.
            const isSameUser = state.user?.user_id === user.user_id;

            return {
              ...state,
              user,
              // An absent slice keeps the persisted value rather than being written
              // as empty: a blank memberCare would read as "not onboarded" and drop
              // the user back into onboarding with the sidebar hidden.
              ...(memberCare !== undefined ? { memberCare } : {}),
              // A different user's leftovers are never carried forward.
              ...(isSameUser
                ? {}
                : {
                    memberCareMembersTab: initialState.memberCareMembersTab,
                    unreadNotifications: undefined,
                  }),
            };
          });
        },
        setMemberCareMembersTab: (newTab) => {
          set((state) => {
            return {
              ...state,
              memberCareMembersTab: newTab,
            };
          });
        },
        setUnreadNotifications: (newUnreadNotifications: number) => {
          set((state) => {
            return {
              ...state,
              unreadNotifications: newUnreadNotifications,
            };
          });
        },
        reset: () => {
          set((state) => ({
            ...state,
            ...initialState,
          }));
        },
      }),
      { name: 'appStore' },
    ),
  ),
);
